import React, { useState } from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import AlertDismissible from '../../../Shared/Components/AlertDismissible'
import FormValidationSummary, { mergeValidationErrors, validationMessage } from '../../../Shared/Components/FormValidationSummary'
import { SIA_NAVY } from '../../../constants/siaTheme'

function todayISO() {
    return new Date().toISOString().slice(0, 10)
}

function formatTime(val) {
    if (!val) return ''
    const s = String(val)
    if (s.length >= 5) return s.slice(0, 5)
    return s
}

function boolToForm(val) {
    return val === true || val === 1 || val === '1'
}

const Reqd = () => <span className="text-danger">*</span>

function YesNoGroup({ label, name, value, onChange, error }) {
    const yes = boolToForm(value)
    return (
        <div className="mb-4">
            <Form.Label className="fw-bold d-block">
                {label} <Reqd />
            </Form.Label>
            <div className="d-flex gap-4">
                <Form.Check
                    type="radio"
                    id={`${name}-si`}
                    name={name}
                    label="Sí"
                    checked={yes}
                    onChange={() => onChange(true)}
                />
                <Form.Check
                    type="radio"
                    id={`${name}-no`}
                    name={name}
                    label="No"
                    checked={!yes}
                    onChange={() => onChange(false)}
                />
            </div>
            {error && <div className="text-danger fs-8 mt-1">{error}</div>}
        </div>
    )
}

const RecruitmentForm = ({ entry, mode, recruiterDisplay, catalogs }) => {
    const { flash, errors: pageErrors } = usePage().props
    const manage = mode === 'manage'
    const isEdit = Boolean(entry)
    const [saving, setSaving] = useState(false)

    const form = useForm({
        fecha: entry?.fecha ? String(entry.fecha).slice(0, 10) : todayISO(),
        nombre: entry?.nombre || '',
        apellido_paterno: entry?.apellido_paterno || '',
        apellido_materno: entry?.apellido_materno || '',
        telefono: entry?.telefono || '',
        zona: entry?.zona || '',
        sia_recruitment_position_id: entry?.sia_recruitment_position_id ? String(entry.sia_recruitment_position_id) : '',
        sia_recruitment_source_id: entry?.sia_recruitment_source_id ? String(entry.sia_recruitment_source_id) : '',
        sia_recruitment_contact_method_id: entry?.sia_recruitment_contact_method_id
            ? String(entry.sia_recruitment_contact_method_id)
            : '',
        observaciones: entry?.observaciones || '',
        cv: null,
        fecha_entrevista: entry?.fecha_entrevista ? String(entry.fecha_entrevista).slice(0, 10) : '',
        hora_entrevista: formatTime(entry?.hora_entrevista),
        fecha_segunda_entrevista: entry?.fecha_segunda_entrevista ? String(entry.fecha_segunda_entrevista).slice(0, 10) : '',
        contesto: boolToForm(entry?.contesto),
        acudio_entrevista: boolToForm(entry?.acudio_entrevista),
        segunda_entrevista: boolToForm(entry?.segunda_entrevista),
        aprobo_veritas: boolToForm(entry?.aprobo_veritas),
        proceso_finalizado: entry ? boolToForm(entry.proceso_finalizado) : false,
        fecha_ingreso: entry?.fecha_ingreso ? String(entry.fecha_ingreso).slice(0, 10) : '',
    })

    const allErrors = mergeValidationErrors(form.errors, pageErrors)
    const fieldError = (key) => validationMessage(allErrors, key)
    const invalidClass = (key) => (fieldError(key) ? 'is-invalid' : '')
    const FieldFeedback = ({ name }) =>
        fieldError(name) ? <div className="invalid-feedback d-block">{fieldError(name)}</div> : null

    const indexRoute = 'recruitment.index'
    const pathPrefix = 'admin/reclutamiento/registros'

    const buildPayload = () => ({
        ...form.data,
        contesto: form.data.contesto ? 1 : 0,
        acudio_entrevista: form.data.acudio_entrevista ? 1 : 0,
        segunda_entrevista: form.data.segunda_entrevista ? 1 : 0,
        aprobo_veritas: form.data.aprobo_veritas ? 1 : 0,
        proceso_finalizado: form.data.proceso_finalizado ? 1 : 0,
    })

    const submit = (e) => {
        e.preventDefault()
        setSaving(true)
        const opts = {
            preserveScroll: true,
            forceFormData: true,
            onFinish: () => setSaving(false),
        }

        form.transform(() => buildPayload())

        if (manage) {
            if (isEdit) {
                form.put(route('recruitment.update', entry.id), opts)
            } else {
                form.post(route('recruitment.store'), opts)
            }
        } else if (isEdit) {
            form.put(route('recruitment.update', entry.id), opts)
        } else {
            form.post(route('recruitment.store'), opts)
        }
    }

    const catalogSelect = (label, field, options) => (
        <div className="mb-4">
            <Form.Label className="fw-bold">
                {label} <Reqd />
            </Form.Label>
            <Form.Select
                value={form.data[field]}
                onChange={(e) => form.setData(field, e.target.value)}
                className={invalidClass(field)}
            >
                <option value="">Seleccione una opción</option>
                {(options || []).map((o) => (
                    <option key={o.id} value={o.id}>
                        {o.name}
                    </option>
                ))}
            </Form.Select>
            <FieldFeedback name={field} />
        </div>
    )

    const cvRequired = !isEdit || !entry?.cv_path

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            <FormValidationSummary errors={allErrors} />
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath={pathPrefix + (isEdit ? `/${entry.id}/edit` : '/create')} />
            </span>

            <div
                className="rounded-2 mb-6 py-4 px-4 text-center text-white text-uppercase fw-bold fs-5"
                style={{ backgroundColor: SIA_NAVY, letterSpacing: '0.04em' }}
            >
                Formulario de registro
            </div>

            <Card className="card-flush shadow-sm mb-10">
                <Card.Body className="py-8 px-6 px-lg-10">
                    <Form onSubmit={submit}>
                        {recruiterDisplay && (
                            <Row className="g-6 mb-6">
                                <Col md={12}>
                                    <Form.Label className="fw-bold text-gray-600">Reclutador</Form.Label>
                                    <div className="form-control form-control-solid bg-light text-uppercase fw-semibold text-gray-800">
                                        {recruiterDisplay.display_name}
                                    </div>
                                    {!isEdit && (
                                        <div className="form-text">Se registrará con el usuario con sesión iniciada.</div>
                                    )}
                                </Col>
                            </Row>
                        )}

                        <Row className="g-6">
                            <Col lg={6}>
                                <div className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Fecha <Reqd />
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={form.data.fecha}
                                        onChange={(e) => form.setData('fecha', e.target.value)}
                                        className={fieldError('fecha') ? 'is-invalid' : ''}
                                    />
                                    {fieldError('fecha') && <div className="invalid-feedback d-block">{fieldError('fecha')}</div>}
                                </div>

                                <div className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Nombre <Reqd />
                                    </Form.Label>
                                    <Form.Control
                                        placeholder="INGRESA EL NOMBRE"
                                        value={form.data.nombre}
                                        onChange={(e) => form.setData('nombre', e.target.value)}
                                        className={fieldError('nombre') ? 'is-invalid' : ''}
                                    />
                                    {fieldError('nombre') && <div className="invalid-feedback d-block">{fieldError('nombre')}</div>}
                                </div>

                                <div className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Apellido paterno <Reqd />
                                    </Form.Label>
                                    <Form.Control
                                        placeholder="INGRESA EL APELLIDO PATERNO"
                                        value={form.data.apellido_paterno}
                                        onChange={(e) => form.setData('apellido_paterno', e.target.value)}
                                        className={fieldError('apellido_paterno') ? 'is-invalid' : ''}
                                    />
                                    {fieldError('apellido_paterno') && (
                                        <div className="invalid-feedback d-block">{fieldError('apellido_paterno')}</div>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Apellido materno <Reqd />
                                    </Form.Label>
                                    <Form.Control
                                        placeholder="INGRESA EL APELLIDO MATERNO"
                                        value={form.data.apellido_materno}
                                        onChange={(e) => form.setData('apellido_materno', e.target.value)}
                                        className={fieldError('apellido_materno') ? 'is-invalid' : ''}
                                    />
                                    {fieldError('apellido_materno') && (
                                        <div className="invalid-feedback d-block">{fieldError('apellido_materno')}</div>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Teléfono <Reqd />
                                    </Form.Label>
                                    <Form.Control
                                        placeholder="INGRESA EL NÚMERO DE TELÉFONO"
                                        value={form.data.telefono}
                                        onChange={(e) => form.setData('telefono', e.target.value)}
                                        className={fieldError('telefono') ? 'is-invalid' : ''}
                                    />
                                    {fieldError('telefono') && <div className="invalid-feedback d-block">{fieldError('telefono')}</div>}
                                </div>

                                {catalogSelect('Puesto', 'sia_recruitment_position_id', catalogs.puestos)}
                                {catalogSelect('Medio de reclutamiento', 'sia_recruitment_source_id', catalogs.medios_reclutamiento)}

                                <div className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Observaciones <Reqd />
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="INGRESA TODAS LAS OBSERVACIONES"
                                        value={form.data.observaciones}
                                        onChange={(e) => form.setData('observaciones', e.target.value)}
                                        className={invalidClass('observaciones')}
                                    />
                                    <FieldFeedback name="observaciones" />
                                </div>

                                <div className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Cargar CV {cvRequired && <Reqd />}
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept=".pdf,.doc,.docx"
                                        onChange={(e) => form.setData('cv', e.target.files[0] || null)}
                                        className={invalidClass('cv')}
                                    />
                                    {entry?.cv_path && (
                                        <div className="text-muted fs-8 mt-2">Archivo actual: {entry.cv_original_filename || 'CV'}</div>
                                    )}
                                    {fieldError('cv') && <div className="text-danger fs-8 mt-1">{fieldError('cv')}</div>}
                                </div>

                                <Row className="g-4 mb-4">
                                    <Col sm={6}>
                                        <Form.Label className="fw-bold">
                                            Fecha de entrevista <Reqd />
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={form.data.fecha_entrevista}
                                            onChange={(e) => form.setData('fecha_entrevista', e.target.value)}
                                            className={invalidClass('fecha_entrevista')}
                                        />
                                        <FieldFeedback name="fecha_entrevista" />
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Label className="fw-bold">
                                            Hora de la entrevista <Reqd />
                                        </Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={form.data.hora_entrevista}
                                            onChange={(e) => form.setData('hora_entrevista', e.target.value)}
                                            className={invalidClass('hora_entrevista')}
                                        />
                                        <FieldFeedback name="hora_entrevista" />
                                    </Col>
                                </Row>
                            </Col>

                            <Col lg={6}>
                                <div className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Fecha de segunda entrevista <Reqd />
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={form.data.fecha_segunda_entrevista}
                                        onChange={(e) => form.setData('fecha_segunda_entrevista', e.target.value)}
                                        className={invalidClass('fecha_segunda_entrevista')}
                                    />
                                    <FieldFeedback name="fecha_segunda_entrevista" />
                                </div>

                                <div className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Zona <Reqd />
                                    </Form.Label>
                                    <Form.Control
                                        placeholder="INGRESA LA ZONA"
                                        value={form.data.zona}
                                        onChange={(e) => form.setData('zona', e.target.value)}
                                        className={fieldError('zona') ? 'is-invalid' : ''}
                                    />
                                    {fieldError('zona') && <div className="invalid-feedback d-block">{fieldError('zona')}</div>}
                                </div>

                                {catalogSelect('Medio de contacto', 'sia_recruitment_contact_method_id', catalogs.medios_contacto)}

                                <YesNoGroup
                                    label="¿Contestó?"
                                    name="contesto"
                                    value={form.data.contesto}
                                    onChange={(v) => form.setData('contesto', v)}
                                    error={fieldError('contesto')}
                                />
                                <YesNoGroup
                                    label="¿Acudió a entrevista?"
                                    name="acudio_entrevista"
                                    value={form.data.acudio_entrevista}
                                    onChange={(v) => form.setData('acudio_entrevista', v)}
                                    error={fieldError('acudio_entrevista')}
                                />
                                <YesNoGroup
                                    label="¿Segunda entrevista?"
                                    name="segunda_entrevista"
                                    value={form.data.segunda_entrevista}
                                    onChange={(v) => form.setData('segunda_entrevista', v)}
                                    error={fieldError('segunda_entrevista')}
                                />
                                <YesNoGroup
                                    label="¿Aprobó veritas?"
                                    name="aprobo_veritas"
                                    value={form.data.aprobo_veritas}
                                    onChange={(v) => form.setData('aprobo_veritas', v)}
                                    error={fieldError('aprobo_veritas')}
                                />
                                <YesNoGroup
                                    label="¿Proceso finalizado?"
                                    name="proceso_finalizado"
                                    value={form.data.proceso_finalizado}
                                    onChange={(v) => form.setData('proceso_finalizado', v)}
                                    error={fieldError('proceso_finalizado')}
                                />

                                <div className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Fecha de ingreso <Reqd />
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={form.data.fecha_ingreso}
                                        onChange={(e) => form.setData('fecha_ingreso', e.target.value)}
                                        className={invalidClass('fecha_ingreso')}
                                    />
                                    <FieldFeedback name="fecha_ingreso" />
                                </div>
                            </Col>
                        </Row>

                        <div className="d-flex flex-column align-items-center gap-4 mt-8">
                            <Button type="submit" variant="primary" size="lg" disabled={saving || form.processing}>
                                {saving ? 'Guardando…' : 'Registrar'}
                            </Button>
                            <Link href={route(indexRoute)} className="btn btn-sm btn-light">
                                ← Regresar al listado
                            </Link>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Row>
    )
}

RecruitmentForm.layout = (page) => <Layout title="Reclutamiento">{page}</Layout>

export default RecruitmentForm
